import { isFocusedFlowPage } from '@/lib/focusedFlow'
import {
    isAnyCustomerDashboardPage,
    isAnySignupPath,
    isAnyStorePage,
    isPharmacyDetailPage,
    isProviderDetailPage,
    isSkuProductDetailPage,
    isStoreHomePage,
    storePrefixPath,
} from '@/lib/namedRoutes'
import { isStoreNavigationDestination } from '@/lib/siteHeader/storeNavigation'
import { siteRoutes } from '@/lib/routes/site'

/**
 * What the top bar should do on a given URL: show or hide, and whether search
 * belongs on mobile and/or desktop.
 *
 * Pages render *inside* the header, so they cannot tell it to hide before the
 * first paint. The header looks at the URL here instead.
 *
 * Search permissions are separate for mobile and desktop so each route can
 * control where search appears without owning the responsive layout. Store
 * and pharmacy / provider detail pages allow both; SKU product detail
 * (`/s/...`, `/dp/...`) is desktop-only. Account routes show the search bar on
 * desktop and a header icon that opens search on mobile.
 */
export interface SiteHeaderConfig {
    /** When false, the header unmounts and only page content renders. */
    visible: boolean
    /**
     * Whether this URL uses the expanded site header that can collapse into
     * the compact bar.
     */
    showExpandedHeader: boolean
    /** Whether this route allows header search on a mobile viewport. */
    showSearchOnMobile: boolean
    /** Whether this route allows header search on a desktop viewport. */
    showSearchOnDesktop: boolean
    /** Whether mobile shows a header icon that opens full-screen search instead of the docked field. */
    showSearchTriggerOnMobile: boolean
    /**
     * Sticky bottom hairline under the compact header.
     */
    showBottomDivider: boolean
    /**
     * Logo-only: no search and no account / sign-in control.
     * Typically used on signup and login.
     */
    logoOnly: boolean
}

/**
 * Fallback when a URL is not in the hide list: show the header without
 * store-only chrome (expanded shop header, search).
 */
const DEFAULT_SITE_HEADER_CONFIG: SiteHeaderConfig = {
    visible: true,
    showExpandedHeader: false,
    showSearchOnMobile: false,
    showSearchOnDesktop: false,
    showSearchTriggerOnMobile: false,
    showBottomDivider: false,
    logoOnly: false,
}

/**
 * Looks at the URL and returns how the header should behave.
 *
 * Hidden means SiteHeader is off. That includes questionnaires and check-in
 * (they have their own back/progress UI). Booking still shows a header; it is
 * just a smaller one later, and is not hidden here.
 */
export function getSiteHeaderConfig(pathname: string): SiteHeaderConfig {
    return {
        ...DEFAULT_SITE_HEADER_CONFIG,
        visible: isVisible(pathname),
        showExpandedHeader: shouldShowExpandedHeader(pathname),
        showSearchOnMobile: shouldShowSearchOnMobile(pathname),
        showSearchOnDesktop: shouldShowSearchOnDesktop(pathname),
        showSearchTriggerOnMobile: shouldShowSearchTriggerOnMobile(pathname),
        showBottomDivider: shouldShowBottomDivider(pathname),
        logoOnly: isLogoOnly(pathname),
    }
}

/** SiteHeader stays off when the page draws its own top bar, or on a focused flow. */
function isVisible(pathname: string): boolean {
    const hiddenPaths: readonly string[] = [
        siteRoutes.generalAi.chat,
        siteRoutes.generalAi.onboarding,
        siteRoutes.generalAi.healthRecords,
        siteRoutes.clinicianHiring,
        siteRoutes.workWithUs,
    ]

    return !hiddenPaths.includes(pathname) && !isFocusedFlowPage(pathname)
}

/** Store home and the shop pill destinations use the expanded header. */
function shouldShowExpandedHeader(pathname: string): boolean {
    if (!isVisible(pathname)) return false

    return isStoreHomePage(pathname) || isStoreNavigationDestination(pathname)
}

/**
 * Storefront and pharmacy / provider directory detail pages show header
 * search on mobile.
 */
function shouldShowSearchOnMobile(pathname: string): boolean {
    return isAnyStorePage(pathname) || isDirectoryDetailPage(pathname)
}

/**
 * Storefront, SKU product detail (`/s/...`, `/dp/...`), pharmacy / provider
 * directory detail, and account pages show header search on desktop.
 */
function shouldShowSearchOnDesktop(pathname: string): boolean {
    return (
        isAnyStorePage(pathname) ||
        isSkuProductDetailPage(pathname) ||
        isDirectoryDetailPage(pathname) ||
        isAnyCustomerDashboardPage(pathname)
    )
}

/** Account routes open search from a header icon because the bottom nav occupies the dock. */
function shouldShowSearchTriggerOnMobile(pathname: string): boolean {
    return isAnyCustomerDashboardPage(pathname)
}

/** Pharmacy and provider directory detail pages (not request flows). */
function isDirectoryDetailPage(pathname: string): boolean {
    return isProviderDetailPage(pathname) || isPharmacyDetailPage(pathname)
}

/** Show a sticky hairline under the compact header. */
function shouldShowBottomDivider(pathname: string): boolean {
    const storeSearchPath = `${storePrefixPath}/search`
    return pathname === storeSearchPath || pathname.startsWith(`${storeSearchPath}/`)
}

/** Signup and login keep the wordmark only */
function isLogoOnly(pathname: string): boolean {
    return isAnySignupPath(pathname)
}
